'use client';

// Galexia Agents — manage standing, scheduled agents: create, wake, pause,
// inspect activity, edit, archive. Plain-fetch data (mirrors the skills/library
// client convention — no SWR/react-query in this app). Inside the app's
// floating rounded-2xl panel shell.

import { useCallback, useEffect, useState } from 'react';
import { Robot, Plus, Sparkle } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ai-elements/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDocumentTitle } from '@/lib/use-document-title';
import { AgentCard } from '@/components/agents/agent-card';
import { AgentDialog } from '@/components/agents/agent-dialog';
import { AgentTriggersDialog } from '@/components/agents/agent-triggers-dialog';
import { AgentTelegramDialog } from '@/components/agents/agent-telegram-dialog';
import { AgentCreateChooser } from '@/components/agents/agent-create-chooser';
import { AgentTemplatePicker, draftFromTemplate } from '@/components/agents/agent-template-picker';
import type { AgentTemplate } from '@/lib/agent-templates';
import type { AgentDraft, AgentSummary } from '@/components/agents/types';

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="rounded-2xl border border-border/60 p-[18px]">
          <div className="flex items-center gap-3">
            <Skeleton className="size-10 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <Skeleton className="mt-4 h-1.5 w-full rounded-full" />
          <Skeleton className="mt-4 h-8 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function AgentsClient() {
  useDocumentTitle('Agents');

  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Creation chooser (chat-with-Galexia vs template vs manual form)
  const [chooserOpen, setChooserOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // Create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AgentSummary | null>(null);
  // Prefill for a fresh create from a template (null = blank create).
  const [initialDraft, setInitialDraft] = useState<AgentDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Per-agent in-flight guard (wake/pause) so buttons disable individually.
  const [busyId, setBusyId] = useState<string | null>(null);

  // Archive confirm
  const [archiveTarget, setArchiveTarget] = useState<AgentSummary | null>(null);

  // Trigger manager
  const [triggersAgent, setTriggersAgent] = useState<AgentSummary | null>(null);

  // Telegram bot manager
  const [telegramAgent, setTelegramAgent] = useState<AgentSummary | null>(null);

  const fetchAgents = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch('/api/agents');
      if (!res.ok) throw new Error(`Failed to load agents (${res.status})`);
      const data = await res.json();
      setAgents(Array.isArray(data.agents) ? data.agents : []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const openCreate = () => {
    setEditing(null);
    setInitialDraft(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (agent: AgentSummary) => {
    setEditing(agent);
    setInitialDraft(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const pickTemplate = (template: AgentTemplate) => {
    setTemplatePickerOpen(false);
    setEditing(null);
    setInitialDraft(draftFromTemplate(template));
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (draft: AgentDraft) => {
    setSaving(true);
    setDialogError(null);
    // Normalize: empty strings -> null for schedule/model/emoji.
    const payload = {
      name: draft.name.trim(),
      emoji: draft.emoji.trim() || null,
      brief: draft.brief.trim(),
      mode: draft.mode,
      schedule: draft.schedule.trim() || null,
      model: draft.model.trim() || null,
      // null = default safe toolset; array = explicit allowlist.
      toolAllowlist: draft.toolAllowlist,
      connectorGrants: draft.connectorGrants,
      dailyBudgetCredits: draft.dailyBudgetCredits,
      maxStepsPerRun: draft.maxStepsPerRun,
      memoryAccess: draft.memoryAccess,
      workspaceEnabled: draft.workspaceEnabled,
      notifyEmail: draft.notifyEmail,
      // '' -> null so an empty override falls back to the owner's email.
      notifyEmailAddress: draft.notifyEmailAddress.trim() || null,
    };
    try {
      const res = await fetch(editing ? `/api/agents/${editing.id}` : '/api/agents', {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Save failed (${res.status})`);
      }
      toast.success(editing ? `Updated “${payload.name}”` : `Created “${payload.name}”`);
      if (data?.scheduleWarning) toast.warning(data.scheduleWarning);
      setDialogOpen(false);
      await fetchAgents();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleWake = async (agent: AgentSummary) => {
    setBusyId(agent.id);
    try {
      const res = await fetch(`/api/agents/${agent.id}/wake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger: 'manual' }),
      });
      const data = await res.json().catch(() => null);
      if (res.status === 202) {
        toast.success(`${agent.name} is waking up…`);
      } else if (res.status === 409) {
        const reason =
          data?.skipped === 'budget'
            ? `Skipped — daily budget reached (${data.spent}/${data.budget} cr)`
            : data?.skipped === 'paused'
              ? 'Skipped — agent is paused'
              : 'Skipped';
        toast.warning(reason);
      } else {
        throw new Error(typeof data?.error === 'string' ? data.error : `Wake failed (${res.status})`);
      }
      await fetchAgents();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Wake failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleTestRun = async (agent: AgentSummary) => {
    setBusyId(agent.id);
    try {
      const res = await fetch(`/api/agents/${agent.id}/wake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger: 'manual', dryRun: true }),
      });
      const data = await res.json().catch(() => null);
      if (res.status === 202) {
        toast.success(`Test run started for ${agent.name}`, {
          description: "It runs once now and won't affect the schedule. It's marked Test in the activity feed.",
        });
      } else if (res.status === 409) {
        const reason =
          data?.skipped === 'budget'
            ? `Skipped — daily budget reached (${data.spent}/${data.budget} cr)`
            : 'Skipped';
        toast.warning(reason);
      } else {
        throw new Error(typeof data?.error === 'string' ? data.error : `Test run failed (${res.status})`);
      }
      await fetchAgents();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test run failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleTogglePause = async (agent: AgentSummary) => {
    const paused = agent.status === 'paused' || agent.status === 'budget_paused';
    // Optimistic status flip.
    const nextStatus = paused ? 'active' : 'paused';
    setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, status: nextStatus } : a)));
    setBusyId(agent.id);
    try {
      const res = await fetch(`/api/agents/${agent.id}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: !paused }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `Update failed (${res.status})`);
      }
      toast.success(paused ? `${agent.name} resumed` : `${agent.name} paused`);
      if (data?.scheduleWarning) toast.warning(data.scheduleWarning);
      await fetchAgents();
    } catch (err) {
      // Revert on failure.
      setAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, status: agent.status } : a)));
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleArchiveConfirm = async () => {
    const agent = archiveTarget;
    if (!agent) return;
    setArchiveTarget(null);
    // Optimistic remove.
    setAgents((prev) => prev.filter((a) => a.id !== agent.id));
    try {
      const res = await fetch(`/api/agents/${agent.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Archive failed (${res.status})`);
      toast.success(`${agent.name} archived`);
      if (data?.scheduleWarning) toast.warning(data.scheduleWarning);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Archive failed');
      await fetchAgents();
    }
  };

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        {/* Sticky header */}
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-2">
            <Robot weight="duotone" className="size-5 text-violet-400" />
            <div>
              <h1 className="text-sm font-extrabold tracking-tight leading-none">Agents</h1>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Standing agents that watch, digest, and draft on a schedule.
              </p>
            </div>
          </div>
          <div className="ml-auto">
            <Button
              size="sm"
              className="h-8 rounded-full bg-violet-500 px-4 text-xs font-extrabold text-white hover:bg-violet-400"
              onClick={() => setChooserOpen(true)}
            >
              <Plus className="mr-1 size-3.5" weight="bold" />
              New agent
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 md:px-6">
          {loadError && (
            <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {loadError}
            </p>
          )}

          {loading ? (
            <SkeletonGrid />
          ) : agents.length === 0 ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <div className="grid size-14 place-items-center rounded-2xl bg-violet-500/12">
                <Robot weight="duotone" className="size-7 text-violet-400" />
              </div>
              <p className="mt-6 text-base font-semibold">Tell Galexia what you want an agent to do</p>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                An agent runs a standing brief on a schedule — a morning digest, a
                source watcher, a weekly recap — spends against a daily budget, and
                reports back to its own transcript. Idle agents cost nothing.
              </p>
              <Button
                size="sm"
                className="mt-6 rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
                onClick={() => setChooserOpen(true)}
              >
                <Sparkle className="mr-1.5 size-4" weight="duotone" />
                Create your first agent
              </Button>
              <button
                type="button"
                onClick={openCreate}
                className="mt-3 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                or set one up manually
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
              {agents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  busy={busyId === agent.id}
                  onWake={handleWake}
                  onTestRun={handleTestRun}
                  onTogglePause={handleTogglePause}
                  onEdit={openEdit}
                  onArchive={setArchiveTarget}
                  onManageTriggers={setTriggersAgent}
                  onManageTelegram={setTelegramAgent}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      <AgentCreateChooser
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        onManual={openCreate}
        onTemplates={() => setTemplatePickerOpen(true)}
      />

      <AgentTemplatePicker
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onPick={pickTemplate}
      />

      <AgentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        initialDraft={initialDraft}
        saving={saving}
        error={dialogError}
        onSubmit={handleSubmit}
      />

      <AgentTriggersDialog
        open={Boolean(triggersAgent)}
        onOpenChange={(open) => { if (!open) setTriggersAgent(null); }}
        agent={triggersAgent ? (agents.find((a) => a.id === triggersAgent.id) ?? triggersAgent) : null}
        onChanged={fetchAgents}
      />

      <AgentTelegramDialog
        open={Boolean(telegramAgent)}
        onOpenChange={(open) => { if (!open) setTelegramAgent(null); }}
        agent={telegramAgent ? (agents.find((a) => a.id === telegramAgent.id) ?? telegramAgent) : null}
        onChanged={fetchAgents}
      />

      <AlertDialog open={Boolean(archiveTarget)} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive agent?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget
                ? `“${archiveTarget.name}” will stop running and disappear from this list. Its transcript and run history are kept.`
                : 'This agent will stop running.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void handleArchiveConfirm()}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
